import { resolveWaylineEllipsoidHeights } from './wayline-kmz'
import type { ParsedWaylinePreview } from './wayline-kmz'

/** Degrees in the original WGS84 frame; never GCJ02 map-preview coordinates. */
export type CesiumWaylineCoordinate2 = [longitude: number, latitude: number]
/** Height is WGS84 ellipsoid metres, with no grounding offset or exaggeration. */
export type CesiumWaylineCoordinate3 = [longitude: number, latitude: number, height: number]

export interface CesiumWaylineRoute<Position> {
  id: string
  heightMode: string
  positions: Position[]
}

export interface CesiumWaylineWaypoint<Position> {
  routeId: string
  /** Zero-based original position within this route, not a globally merged index. */
  index: number
  position: Position
  /** Height as written in the file (takeoff-relative when known), for the map label; '' when unknown. */
  heightLabel: string
  /** Human readable summary of the configured actions; '' when none. */
  actionLabel: string
}

const ACTION_NAMES: Record<string, string> = {
  takePhoto: '拍照',
  startRecord: '开始录像',
  stopRecord: '结束录像',
  hover: '悬停',
  gimbalRotate: '云台转动',
  gimbalEvenlyRotate: '云台匀速转动',
  rotateYaw: '飞行器偏航',
  zoom: '变焦',
  focus: '对焦',
  customDirName: '新建文件夹',
  panoShot: '全景拍照',
  orientedShoot: '定角拍照',
  accurateShoot: 'AI 复拍',
  multipleTiming: '等时拍照',
  multipleDistance: '等距拍照',
}

/** Collapse repeated actions ("拍照×2") and translate WPML actuator names. */
export function describeWaylineActions (actions: string[] | undefined): string {
  if (!actions || !actions.length) return ''
  const counts = new Map<string, number>()
  actions.forEach(action => counts.set(action, (counts.get(action) || 0) + 1))
  return Array.from(counts.entries()).map(([action, count]) => (ACTION_NAMES[action] || action) + (count > 1 ? `×${count}` : '')).join('、')
}

export interface CesiumWaylineFit {
  west: number
  south: number
  east: number
  north: number
  /** west > east is the short envelope crossing the antimeridian. */
  crossesAntimeridian: boolean
  minHeight: number | null
  maxHeight: number | null
}

interface CesiumWaylinePlanBase<Position> {
  routes: Array<CesiumWaylineRoute<Position>>
  waypoints: Array<CesiumWaylineWaypoint<Position>>
  takeoffCoordinate: CesiumWaylineCoordinate2 | null
  takeoffFallback: boolean
  takeoffPath: Position[]
  fit: CesiumWaylineFit | null
}

export type CesiumWaylinePlan =
  | (CesiumWaylinePlanBase<CesiumWaylineCoordinate3> & {
    mode: '3d'
    takeoffHeight: number | null
    /** Height of the takeoff point above the ground under it (WPML takeOffRefPointAGLHeight), when the file states it. */
    takeoffAgl?: number | null
    warning: null
  })
  | (CesiumWaylinePlanBase<CesiumWaylineCoordinate2> & {
    mode: '2d'
    /** A 2D plan deliberately exposes no renderable altitude, even if some are known. */
    takeoffHeight: null
    warning: string
  })

const UNKNOWN_HEIGHT_WARNING = '航线缺少可确认的 WGS84 椭球高度基准，仅显示二维经纬度预览，不能与模型比较高度。'

/**
 * Pure preparation for a single host-owned Cesium viewer. Input is rawParsedPreview,
 * before any AMap conversion. The renderer owns entities, camera, clamping policy,
 * persistence and disposal; this module never accesses a map or modifies its source.
 */
export function prepareCesiumWaylinePreview (raw: ParsedWaylinePreview): CesiumWaylinePlan {
  const absolute = resolveWaylineEllipsoidHeights(raw)
  const takeoffCoordinate = raw.takeoff ? coordinate2(raw.takeoff.coordinate) : null
  const takeoffFallback = Boolean(raw.takeoff?.fallback)
  const pointCount = raw.routes.reduce((count, route) => count + route.points.length, 0)
  // A finite relative height plus a finite takeoff datum can still overflow.
  const canRender3D = absolute.canRender3D && absolute.preview.routes.every(route =>
    route.points.every(point => Number.isFinite(point.height)))

  if (canRender3D) {
    const routes = absolute.preview.routes.map(route => ({
      id: route.id,
      heightMode: route.heightMode,
      positions: route.points.map(point => [...coordinate2(point.coordinate), point.height] as CesiumWaylineCoordinate3),
    }))
    const waypoints = collectWaypoints(routes, raw)
    const first = waypoints[0].position
    const takeoffHeight = absolute.takeoffHeight
    const takeoffPath: CesiumWaylineCoordinate3[] = takeoffCoordinate && takeoffHeight !== null
      ? [[...takeoffCoordinate, takeoffHeight], [...takeoffCoordinate, first[2]], [...first]]
      : []
    const positions = waypoints.map(point => point.position).concat(takeoffPath)
    const takeoffAgl = raw.takeoff?.altitudeHeight
    return {
      mode: '3d',
      routes,
      waypoints,
      takeoffCoordinate,
      takeoffFallback,
      takeoffHeight,
      takeoffAgl: typeof takeoffAgl === 'number' && Number.isFinite(takeoffAgl) ? takeoffAgl : null,
      takeoffPath,
      fit: fitBounds(positions, takeoffCoordinate, positions.map(point => point[2])),
      warning: null,
    }
  }

  const routes = raw.routes.map(route => ({
    id: route.id,
    heightMode: route.heightMode,
    positions: route.points.map(point => coordinate2(point.coordinate)),
  }))
  const waypoints = collectWaypoints(routes, raw)
  const first = waypoints[0]?.position
  // Only a horizontal connector is meaningful when flight altitude is unresolved.
  const takeoffPath: CesiumWaylineCoordinate2[] = takeoffCoordinate && first
    ? [[...takeoffCoordinate], [...first]]
    : []
  return {
    mode: '2d',
    routes,
    waypoints,
    takeoffCoordinate,
    takeoffFallback,
    takeoffHeight: null,
    takeoffPath,
    fit: first ? fitBounds(waypoints.map(point => point.position), takeoffCoordinate, null) : null,
    warning: pointCount ? UNKNOWN_HEIGHT_WARNING : '航线没有可展示的航点。',
  }
}

function coordinate2 (coordinate: readonly unknown[]): CesiumWaylineCoordinate2 {
  const [longitude, latitude] = coordinate
  if (typeof longitude !== 'number' || typeof latitude !== 'number' ||
    !Number.isFinite(longitude) || !Number.isFinite(latitude) || Math.abs(longitude) > 180 || Math.abs(latitude) > 90) {
    throw new Error('航线包含无效的 WGS84 经纬度。')
  }
  return [longitude, latitude]
}

function collectWaypoints<Position> (routes: Array<CesiumWaylineRoute<Position>>, raw: ParsedWaylinePreview): Array<CesiumWaylineWaypoint<Position>> {
  return routes.flatMap((route, routeIndex) => route.positions.map((position, index) => {
    const source = raw.routes[routeIndex]?.points[index]
    const heightLabel = source && !source.missingHeight && Number.isFinite(source.height) ? `${Math.round(source.height * 10) / 10} m` : ''
    return { routeId: route.id, index, position, heightLabel, actionLabel: describeWaylineActions(source?.actions) }
  }))
}

function fitBounds (
  positions: Array<CesiumWaylineCoordinate2 | CesiumWaylineCoordinate3>,
  takeoff: CesiumWaylineCoordinate2 | null,
  heights: number[] | null
): CesiumWaylineFit {
  const coordinates = takeoff ? [...positions, takeoff] : positions
  // Removing the largest circular gap gives the shortest envelope, including
  // routes around +/-180 degrees instead of flying the camera across the globe.
  const longitudes = Array.from(new Set(coordinates.map(point => (point[0] + 360) % 360))).sort((left, right) => left - right)
  let largestGap = -1
  let start = 0
  for (let index = 0; index < longitudes.length; index++) {
    const next = index + 1 === longitudes.length ? longitudes[0] + 360 : longitudes[index + 1]
    if (next - longitudes[index] > largestGap) {
      largestGap = next - longitudes[index]
      start = (index + 1) % longitudes.length
    }
  }
  const signed = (longitude: number) => longitude >= 180 ? longitude - 360 : longitude
  const west = signed(longitudes[start])
  const east = signed(longitudes[(start + longitudes.length - 1) % longitudes.length])
  let south = Infinity
  let north = -Infinity
  for (const point of coordinates) { south = Math.min(south, point[1]); north = Math.max(north, point[1]) }
  let minHeight: number | null = null
  let maxHeight: number | null = null
  if (heights) {
    for (const height of heights) {
      minHeight = minHeight === null ? height : Math.min(minHeight, height)
      maxHeight = maxHeight === null ? height : Math.max(maxHeight, height)
    }
  }
  return { west, south, east, north, crossesAntimeridian: west > east, minHeight, maxHeight }
}
